import { useCallback, useMemo, useState } from "react";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import SelectionDeleter from "../../../../components/TableComponents/SelectionDeleter";
import { deleteReseller } from "../../../../api/UserAPI";
import {
  ActionIcon,
  Anchor,
  Divider,
  Flex,
  Group,
  NumberFormatter,
  Title,
  Tooltip,
} from "@mantine/core";
import numberColumn from "../../../../layouts/Table/columns/numberColumn";
import { nameColumn } from "../../../../layouts/Table/columns/nameColumn";
import MainTable from "../../../../layouts/Table/MainTable";
import { IconEdit, IconExternalLink } from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import { getResellers } from "../../../../api/AdminAPI";
import { useLanguage } from "../../../../stores/LanguageStore";
import { useListingLoader } from "../../../../components/hooks/ListingHooks";
import DomainsCell from "../../resellers/DomainsCell";
import ResellerExtension from "../../resellers/howto/ResellerExtension";

const ResellersListingAdmin = () => {
  const lang = useLanguage((s) => s.language);
  const apiRef = useGridApiRef();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const columns = useMemo(() => {
    let result = [
      numberColumn(),

      nameColumn({
        parentPath: (row) => `/resellers/${row.id}/users`,
        withoutId: true,
        rightSection: (row) => (
          <Group gap={4} wrap="nowrap">
            <Anchor
              target={"_blank"}
              href={`https://${row?.domain_saas2 || row.domain_saas}`}
            >
              <ActionIcon variant="subtle">
                <IconExternalLink size={17} />
              </ActionIcon>
            </Anchor>

            <ResellerExtension id={row.id} inTable name={row.name} />
          </Group>
        ),
      }),
      {
        field: "number_of_users",
        headerName: lang.resellers.columns.number_of_users,
        width: 150,
        type: "number",
        align: "left",
        headerAlign: "left",
        renderCell: ({ value, row }) => (
          <>
            {value ? (
              <NumberFormatter
                value={value}
                thousandSeparator=" "
                style={{ marginRight: 12, fontWeight: 600 }}
              />
            ) : (
              <span style={{ marginRight: 12, fontWeight: 600 }}>1</span>
            )}
            (
            <NumberFormatter
              value={row?.number_of_users_activated || 0}
              thousandSeparator=" "
              style={{ marginLeft: 2 }}
            />{" "}
            <span style={{ marginLeft: 4, marginRight: 2 }}>
              {lang.resellers.columns.active}
            </span>
            )
          </>
        ),
      },
      {
        field: "domain_saas",
        headerName: lang.resellers.columns.domain_saas,
        width: 180,
        renderCell: ({ row, value }) => (
          <DomainsCell status={row.domain_saas_status} value={value} />
        ),
      },
      {
        field: "domain_saas2",
        headerName: lang.resellers.columns.domain_saas + " 2",
        width: 180,
        renderCell: ({ row, value }) => (
          <DomainsCell status={row.domain_saas2_status} value={value} />
        ),
      },
      {
        field: "domain_saas3",
        headerName: lang.resellers.columns.domain_saas + " 3",
        width: 180,
        renderCell: ({ row, value }) => (
          <DomainsCell status={row.domain_saas3_status} value={value} />
        ),
      },
      {
        field: "domain_files",
        headerName: lang.resellers.columns.domain_files,
        width: 180,
        renderCell: ({ row, value }) => (
          <DomainsCell status={row.domain_files_status} value={value} />
        ),
      },
      {
        field: "domain_redirect",
        headerName: lang.resellers.columns.domain_redirect,
        width: 180,
        renderCell: ({ row, value }) => (
          <DomainsCell status={row.domain_redirect_status} value={value} />
        ),
      },
      {
        field: "domain_api",
        headerName: lang.resellers.columns.domain_api,
        width: 180,
        renderCell: ({ row, value }) => (
          <DomainsCell status={row.domain_api_status} value={value} />
        ),
      },
    ];

    return result;
  }, [lang]);

  const actionsCell = useCallback(
    ({ row }) => {
      return (
        <Group wrap="nowrap" gap={4}>
          <Tooltip label={lang.global.edit}>
            <ActionIcon
              component={Link}
              to={"/resellers/" + row.id + "/settings"}
              variant="subtle"
              onClick={e => e.stopPropagation()}
            >
              <IconEdit size={18} />
            </ActionIcon>
          </Tooltip>
          <ResellerExtension inTable id={row.id} name={row.name} />
          {/* <ItemDeleter
            title_bold={`${row.name} #${row.id}`}
            handleDelete={deleteReseller}
            deletedMessage={lang.resellers.delete_success}
            id={row.id}
            apiRef={apiRef}
          /> */}
        </Group>
      );
    },
    [apiRef, lang.global.edit, lang.resellers.delete_success]
  );

  const toolbarActions = useCallback(
    ({ selectedIds }) => (
      <SelectionDeleter
        handleDelete={deleteReseller}
        apiRef={apiRef}
        setTableLoading={setLoading}
        ids={selectedIds}
      />
    ),
    [apiRef]
  );

  const navigate = useNavigate();
  const onRowClick = ({ row }) => {
    navigate("/resellers/" + row.id);
  };

  useListingLoader({
    loading,
    setLoading,
    listGetter: getResellers,
    responseField: "domains_list",
    setRows,
    getRows: (data) => {
      let final = [];
      data.forEach((element) => {
        let same_ids = data.filter(
          (x) => x.reseller_id === element.reseller_id
        );
        let add = {
          id: element.reseller_id,
          name: element.name,
          domain_saas: same_ids.find((x) => x.type === "saas")?.domain,
          domain_saas2: same_ids.find((x) => x.type === "saas2")?.domain,
          domain_saas3: same_ids.find((x) => x.type === "saas3")?.domain,
          domain_saas_status: same_ids.find((x) => x.type === "saas")?.status,
          domain_saas2_status: same_ids.find((x) => x.type === "saas2")?.status,
          domain_saas3_status: same_ids.find((x) => x.type === "saas3")?.status,
          domain_saas_status_date: same_ids.find((x) => x.type === "saas")
            ?.domain_saas_status_date,

          domain_files: same_ids.find((x) => x.type === "files")?.domain,
          domain_files_status: same_ids.find((x) => x.type === "files")?.status,
          domain_files_status_date: same_ids.find((x) => x.type === "files")
            ?.domain_saas_status_date,

          domain_redirect: same_ids.find((x) => x.type === "redirect")?.domain,
          domain_redirect_status: same_ids.find((x) => x.type === "redirect")
            ?.status,
          domain_redirect_status_date: same_ids.find(
            (x) => x.type === "redirect"
          )?.domain_saas_status_date,

          domain_api: same_ids.find((x) => x.type === "api")?.domain,
          domain_api_status: same_ids.find((x) => x.type === "api")?.status,
          domain_api_status_date: same_ids.find((x) => x.type === "api")
            ?.domain_saas_status_date,
          number_of_users: element.number_of_users,
          number_of_users_activated: element.number_of_users_activated,
        };

        if (!final.find((x) => x.id === element.reseller_id)) final.push(add);
      });

      return final;
    },
  });

  return (
    <>
      <Divider mt={32} />
      <Title order={3} mt={32} mb={16}>
        Toutes les marques blanches de la plateforme
      </Title>

      <Flex h={450}>
        <MainTable
          apiRef={apiRef}
          columns={columns}
          rows={rows}
          loading={loading}
          setLoading={setLoading}
          id={"list-table"}
          onRowClick={onRowClick}
          toolbarActions={toolbarActions}
          actionsCell={actionsCell}
          whiteButtons
          pageOrderName={"resellers_order"}
        />
      </Flex>
    </>
  );
};

export default ResellersListingAdmin;
